/**
 * MED-1269 — Dev-only harness for the Edit Cohort Details deferred-save work.
 *
 * Renders the REAL EditCohortDetailsModal against an in-memory cohort fixture
 * with the API layer mocked so nothing hits the backend. Every API call is
 * logged to an on-screen panel, so you can watch that the three converted
 * sections (Audience Feature Controls, Email Settings, Session Time Slot
 * Options) write NOTHING until you click Save, then commit in one batch.
 *
 * No auth, no DB. Compiled out of production builds. Visit /dev/edit-cohort-save.
 *
 * Verify:
 *   - Toggle any control in the three sections → the log stays quiet (no write).
 *   - Cancel / Escape / backdrop with pending changes → discard prompt (AC7).
 *   - Toggle an audience feature OFF, or stage a slot delete, then Save →
 *     one future-tense summary confirm (AC11), then batched writes in the log.
 *   - Save with no changes closes with no writes.
 */
import {
  Component,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Box,
  Button,
  Code,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { EditCohortDetailsModal } from '../cohorts/components/edit-cohort/EditCohortDetailsModal';
import { cohortsApi, type FeatureToggles } from '../../services/api/cohorts';
import { timeSlotsApi } from '../../services/api/timeSlots';
import { partnersApi } from '../../services/api/partners';
import { usersApi } from '../../services/api/users';
import { jobTitlesApi } from '../../services/api/jobTitles';
import type {
  CohortEmailSettings,
  CohortWithDetails,
  TimeSlotResponse,
} from '../../types';

// ---- In-memory fixtures (mutated by the mocked write endpoints) ----

const COHORT_ID = 'dev-cohort-1269';

const makeCohort = (): CohortWithDetails =>
  ({
    id: COHORT_ID,
    program_id: 'dev-program',
    name: 'Deferred Save Demo Cohort',
    short_name: 'DSD',
    code: 'DSD01',
    description: null,
    cohort_type: 'standard',
    status: 'draft',
    start_date: '2026-09-01',
    end_date: '2026-11-01',
    preferred_days: [],
    preferred_times: [],
    participant_capacity: 20,
    is_active: true,
    created_at: '2026-01-01T00:00:00.000Z',
    updated_at: '2026-01-01T00:00:00.000Z',
    coach_immersion_date: null,
    participant_orientation_date: null,
    coach_huddle_dates: [],
    cohort_wrap_up_date: null,
    default_meeting_link: null,
    default_meeting_platform: null,
    time_slot_display_timezones: [],
    disabled_email_recipients: [],
    email_settings: {
      master_enabled: true,
      personas: {},
    } as unknown as CohortEmailSettings,
    audience_type: null,
    participant_description: null,
    matching_form_link: null,
    matching_form_deadline: null,
    info_session_date: null,
    participant_features_enabled: true,
    coach_features_enabled: true,
    program_name: 'Dev Program',
    partner_id: 'dev-partner',
    partner_name: 'Dev Partner',
    partner_code: 'DP',
    participant_count: 0,
    modalities: [],
    sessions: [],
    date_constraints: [],
    job_title_assignments: [],
    primary_contacts: [],
  }) as unknown as CohortWithDetails;

const featureToggles: FeatureToggles = {
  participant_features_enabled: true,
  coach_features_enabled: true,
  participant_app_access_enabled: true,
};

const emailSettings: CohortEmailSettings = {
  master_enabled: true,
  personas: {
    participants: { enabled: true, emails: {} },
    coaches: { enabled: true, emails: {} },
    partners: { enabled: true, emails: {} },
  },
} as unknown as CohortEmailSettings;

const makeSlot = (n: number, day: string, time: string): TimeSlotResponse =>
  ({
    id: `slot-${n}`,
    cohort_id: COHORT_ID,
    identifier: `GC${n}`,
    slot_number: n,
    day_of_week: day,
    time,
    timezone: 'America/New_York',
    created_at: '2026-01-01T00:00:00.000Z',
    updated_at: '2026-01-01T00:00:00.000Z',
    is_deleted: false,
    display_time: time,
    display_day: day,
    timezone_abbreviation: 'ET',
  }) as unknown as TimeSlotResponse;

const slotStore: TimeSlotResponse[] = [
  makeSlot(1, 'monday', '09:00'),
  makeSlot(2, 'wednesday', '13:00'),
];
let ftStore: FeatureToggles = { ...featureToggles };
let emailStore: CohortEmailSettings = JSON.parse(JSON.stringify(emailSettings));

type LogFn = (line: string) => void;

// Reassign the API singleton methods to logged stubs. Returns a restore fn.
function installMocks(log: LogFn): () => void {
  const snap = {
    cohortsApi: { ...cohortsApi },
    timeSlotsApi: { ...timeSlotsApi },
    partnersApi: { ...partnersApi },
    usersApi: { ...usersApi },
    jobTitlesApi: { ...jobTitlesApi },
  };

  const write = (name: string, payload?: unknown) =>
    log(
      `✍️  WRITE  ${name}${payload !== undefined ? ' ' + JSON.stringify(payload) : ''}`
    );
  const read = (name: string) => log(`· read   ${name}`);

  // Reads used on load — quiet "read" lines so writes stand out.
  cohortsApi.getFeatureToggles = async () => {
    read('getFeatureToggles');
    return { ...ftStore };
  };
  cohortsApi.getEmailSettings = async () => {
    read('getEmailSettings');
    return JSON.parse(JSON.stringify(emailStore));
  };
  cohortsApi.getById = async () => {
    read('getById');
    return makeCohort();
  };
  partnersApi.listContacts = async () => {
    read('partners.listContacts');
    return [];
  };
  usersApi.listInternal = async () => {
    read('users.listInternal');
    return [];
  };
  jobTitlesApi.getAll = async () => {
    read('jobTitles.getAll');
    return [];
  };
  timeSlotsApi.list = async () => {
    read('timeSlots.list');
    return { time_slots: slotStore.map(s => ({ ...s })) } as never;
  };
  timeSlotsApi.getUsage = async () => {
    read('timeSlots.getUsage');
    return {
      isAssignedToGroup: false,
      availabilityRequestCount: 0,
      canDelete: true,
    } as never;
  };

  // Writes — these must NOT fire until Save.
  cohortsApi.updateFeatureToggles = async (_id, diff) => {
    write('updateFeatureToggles', diff);
    ftStore = { ...ftStore, ...(diff as Partial<FeatureToggles>) };
    return { ...ftStore };
  };
  cohortsApi.updateEmailSettings = async (_id, updated) => {
    write('updateEmailSettings');
    emailStore = JSON.parse(JSON.stringify(updated));
    return JSON.parse(JSON.stringify(emailStore));
  };
  timeSlotsApi.create = async (_id, req) => {
    write('timeSlots.create', req);
    return {} as never;
  };
  timeSlotsApi.update = async (_id, slotId, req) => {
    write('timeSlots.update', { slotId, ...req });
    return {} as never;
  };
  timeSlotsApi.delete = async (_id, slotId) => {
    write('timeSlots.delete', { slotId });
    return {} as never;
  };
  cohortsApi.update = async () => {
    write('cohorts.update');
    return makeCohort();
  };
  cohortsApi.updatePrimaryContacts = async () => {
    write('cohorts.updatePrimaryContacts');
    return makeCohort();
  };
  cohortsApi.updateDateConstraints = async () => {
    write('cohorts.updateDateConstraints');
    return makeCohort();
  };
  cohortsApi.updateSessions = async () => {
    write('cohorts.updateSessions');
    return makeCohort();
  };
  cohortsApi.updateModalities = async () => {
    write('cohorts.updateModalities');
    return makeCohort();
  };
  cohortsApi.updateModalitySettings = async () => {
    write('cohorts.updateModalitySettings');
    return {} as never;
  };
  cohortsApi.regenerateDueDates = async () => {
    write('cohorts.regenerateDueDates');
    return { updatedCount: 0, createdCount: 0 } as never;
  };
  cohortsApi.regenerateTeamAssignments = async () => {
    write('cohorts.regenerateTeamAssignments');
    return { updatedCount: 0 } as never;
  };
  jobTitlesApi.updateCohortAssignments = async () => {
    write('jobTitles.updateCohortAssignments');
    return [] as never;
  };

  return () => {
    Object.assign(cohortsApi, snap.cohortsApi);
    Object.assign(timeSlotsApi, snap.timeSlotsApi);
    Object.assign(partnersApi, snap.partnersApi);
    Object.assign(usersApi, snap.usersApi);
    Object.assign(jobTitlesApi, snap.jobTitlesApi);
  };
}

class HarnessErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <Paper
          withBorder
          p="md"
          mt="md"
          style={{ borderColor: 'var(--mantine-color-red-6)' }}
        >
          <Text fw={600} c="red">
            Modal crashed during render:
          </Text>
          <Code block mt="xs">
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </Code>
        </Paper>
      );
    }
    return this.props.children;
  }
}

export function EditCohortSavePreviewPage() {
  const [log, setLog] = useState<string[]>([]);
  // Start closed and open via an effect (a post-mount state change), matching
  // how the real app opens it — mounting a Mantine Modal already-open on the
  // first render trips its focus-trap flushSync path.
  const [opened, setOpened] = useState(false);
  const [mocksReady, setMocksReady] = useState(false);
  const restoreRef = useRef<(() => void) | null>(null);

  const append: LogFn = line =>
    setLog(prev => [
      ...prev,
      `${new Date().toISOString().substring(11, 23)}  ${line}`,
    ]);

  useEffect(() => {
    restoreRef.current = installMocks(append);
    setMocksReady(true);
    setOpened(true);
    return () => restoreRef.current?.();
  }, []);

  // Stable identity — the modal's form hook keys its load effect on the cohort
  // prop, so a fresh object each render would re-fire the effect forever.
  const cohort = useMemo(() => makeCohort(), []);

  return (
    <Box p="lg">
      <Stack gap="md">
        <Title order={3}>
          MED-1269 — Edit Cohort Details deferred-save harness
        </Title>
        <Text size="sm" c="dimmed">
          The modal below is the real component with a mocked API. Toggle
          controls in <b>Audience Feature Controls</b>, <b>Email Settings</b>,
          and <b>Session Time Slot Options</b>. Nothing should log a{' '}
          <Code>WRITE</Code> line until you click <b>Save Changes</b>. Cancel /
          Escape / backdrop with pending changes should prompt before
          discarding.
        </Text>
        <Group>
          <Button onClick={() => setOpened(true)} disabled={opened}>
            Reopen modal
          </Button>
          <Button variant="default" onClick={() => setLog([])}>
            Clear log
          </Button>
        </Group>

        <Paper withBorder p="md" radius="md">
          <Text fw={600} size="sm" mb="xs">
            API call log ({log.length})
          </Text>
          <ScrollArea h={220}>
            <Stack gap={2}>
              {log.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No calls yet.
                </Text>
              ) : (
                log.map((line, i) => (
                  <Text
                    key={i}
                    size="xs"
                    ff="monospace"
                    c={line.includes('WRITE') ? 'red' : 'dimmed'}
                  >
                    {line}
                  </Text>
                ))
              )}
            </Stack>
          </ScrollArea>
        </Paper>
      </Stack>

      {mocksReady && (
        <HarnessErrorBoundary>
          <EditCohortDetailsModal
            opened={opened}
            onClose={() => {
              append('modal onClose()');
              setOpened(false);
            }}
            cohort={cohort}
            onUpdate={() => append('onUpdate(cohort) — parent refreshed')}
            initialExpandedSections={[
              'feature-toggles',
              'email-settings',
              'time-slots',
            ]}
          />
        </HarnessErrorBoundary>
      )}
    </Box>
  );
}

export default EditCohortSavePreviewPage;
